// Iter78 — Public "All Approved Partner Stores" listing page.
// Bare minimum surface so the View All link from /shop has a destination
// when more than 10 partners exist. Reuses /api/storefront/partners.
import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import api, { apiErrorMessage } from "@/lib/api";
import SafeImage from "@/components/SafeImage";
import PageMeta from "@/components/PageMeta";

export default function PartnersListing() {
  const [partners, setPartners] = useState([]);
  const [loading, setLoading]   = useState(true);
  const [err, setErr]           = useState("");

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get("/storefront/partners");
        if (cancelled) return;
        const list = Array.isArray(data?.partners) ? data.partners : [];
        setPartners(list.filter((p) => p.business_name && p.business_name.trim()));
      } catch (e) {
        if (!cancelled) setErr(apiErrorMessage(e));
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  return (
    <main className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-10 py-10 space-y-6"
          data-testid="public-partners-listing">
      <PageMeta
        title="Approved Partner Stores — Lifted At The Moment"
        description="Browse every approved partner store on the Lifted network."
      />
      <Link to="/shop" className="inline-flex items-center gap-1 text-xs uppercase tracking-[0.2em] text-lifted-dim hover:text-lifted-green">
        <ArrowLeft className="h-3 w-3" /> Back to shop
      </Link>
      <header>
        <div className="eyebrow text-lifted-green">Lifted Partners</div>
        <h1 className="font-anton text-4xl uppercase text-white">Approved Partner Stores</h1>
        <p className="text-lifted-dim text-sm mt-2 max-w-2xl">
          The full directory of partner stores currently selling through Lifted.
          Tap any logo to open that partner's storefront.
        </p>
      </header>

      {loading ? (
        <div className="text-lifted-dim text-sm">Loading partners…</div>
      ) : err ? (
        <div className="text-rose-300 text-sm" data-testid="public-partners-error">{err}</div>
      ) : partners.length === 0 ? (
        <div className="text-lifted-dim text-sm" data-testid="public-partners-empty">
          No approved partner stores yet — check back soon.
        </div>
      ) : (
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3"
             data-testid="public-partners-grid">
          {partners.map((p) => (
            <Link
              key={p.id}
              to={`/shop/partner/${p.slug}`}
              data-testid={`public-partners-card-${p.slug}`}
              className="group glass-panel p-3 hover:border-lifted-green/50 transition flex flex-col items-center text-center"
            >
              <div className="w-full aspect-square rounded-md bg-white/5 grid place-items-center overflow-hidden mb-2 p-2">
                <SafeImage
                  src={p.logo_url}
                  alt={p.business_name || "Partner logo"}
                  className="max-w-full max-h-full w-auto h-auto object-contain"
                />
              </div>
              <div className="text-xs font-anton uppercase text-white truncate w-full">
                {p.business_name || "Lifted Partner"}
              </div>
              {p.city && <div className="text-[10px] text-lifted-dim font-mono">{p.city}</div>}
            </Link>
          ))}
        </div>
      )}
    </main>
  );
}
